import { type ReactNode, useCallback, useMemo, useState } from 'react';

import {
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core';

import { RiPauseLine, RiPlayLine } from '@remixicon/react';

import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';
import { MetricEventName } from '@spotify-internal/csr-player';
import {
  ChipFilter,
  ChipGroup,
  IconFilter,
} from '@spotify-internal/encore-web';

import { formatPlaybackTime } from '../../utils';
import { EventMarker } from './EventMarker';
import { SpeedMenu } from './SpeedMenu';
import { TimelineSlider } from './TimelineSlider';
import type { PageSegment } from './types';
import { useEventCategories } from './useEventCategories';
import { getRouteLabel, useEventMarkers } from './useEventMarkers';
import { type PlaybackDefaults, usePlaybackState } from './usePlaybackState';

import styles from './FullPlaybackControls.module.css';

type FullPlaybackControlsProps = {
  player: CsrPlayerElement;
  metricEvents: MetricEvent[];
  initialPageUrl?: string;
  playbackDefaults?: PlaybackDefaults;
  onEventMarkerClick?: (timestampSeconds: number) => void;
  fullscreenButton?: ReactNode;
  menuContainer?: HTMLElement;
};

export const FullPlaybackControls = ({
  player,
  metricEvents,
  initialPageUrl,
  playbackDefaults,
  onEventMarkerClick,
  fullscreenButton,
  menuContainer,
}: FullPlaybackControlsProps) => {
  const [activeCategories, setActiveCategories] = useState<Set<string>>(
    () => new Set(),
  );

  const {
    isPlaying,
    currentTimeMs,
    totalTimeMs,
    speed,
    skipInactive,
    handleSeek,
    handleSpeedChange,
    handleToggleSkipInactive,
  } = usePlaybackState(player, { defaults: playbackDefaults });

  const { categories: eventCategories, getEventColor } =
    useEventCategories(metricEvents);

  const getMarkerColor = useCallback(
    (event: MetricEvent) => getEventColor(event.name),
    [getEventColor],
  );

  const { markers: eventMarkers, handleMarkerClick } = useEventMarkers(
    metricEvents,
    player,
    {
      getColor: getMarkerColor,
      getLabel: getRouteLabel,
      onMarkerClick: onEventMarkerClick,
      onSeek: handleSeek,
    },
  );

  const nonRouteMarkers = useMemo(
    () => eventMarkers.filter(m => m.name !== MetricEventName.RouteChange),
    [eventMarkers],
  );

  const nonRouteCategories = useMemo(
    () => eventCategories.filter(c => c.id !== MetricEventName.RouteChange),
    [eventCategories],
  );

  const visibleMarkers = useMemo(
    () => nonRouteMarkers.filter(m => activeCategories.has(m.name)),
    [nonRouteMarkers, activeCategories],
  );

  const toggleCategory = (id: string) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const pageSegments = useMemo<PageSegment[]>(() => {
    const routeChanges = metricEvents.filter(
      (e): e is MetricEvent & { name: typeof MetricEventName.RouteChange } =>
        e.name === MetricEventName.RouteChange,
    );

    const firstUrl =
      initialPageUrl ?? (routeChanges[0]?.data?.from || 'Unknown');

    const segments: PageSegment[] = [
      { startPercent: 0, url: firstUrl, timestampMs: 0 },
    ];

    for (const event of routeChanges) {
      segments.push({
        startPercent: (event.timestamp / player.duration) * 100,
        url: event.data?.to || 'Unknown',
        timestampMs: event.timestamp * 1000,
      });
    }

    return segments;
  }, [metricEvents, player, initialPageUrl]);

  const progressPercent =
    totalTimeMs > 0 ? (currentTimeMs / totalTimeMs) * 100 : 0;

  const handlePlayPause = () => {
    if (isPlaying) {
      player.pause();
    } else {
      player.play();
    }
  };

  const currentPage = pageSegments.findLast(
    s => s.startPercent <= progressPercent,
  );

  return (
    <div className={styles.root}>
      {nonRouteCategories.length > 0 && (
        <div className={styles.categoryRow}>
          <IconFilter
            size="small"
            semanticColor="textSubdued"
            aria-label="Event type filters"
            className={styles.categoryFilterIcon}
          />
          <ChipGroup
            aria-label="Event categories"
            mode="multi-select"
            layout="multi-line"
          >
            {nonRouteCategories.map(cat => (
              <ChipFilter
                key={cat.id}
                size="small"
                className={styles.categoryChip}
                isSelected={activeCategories.has(cat.id)}
                selectedVariant="bordered"
                data-track-id="button.event-filter"
                data-track-action="update"
                data-track-data-event-type={cat.id}
                data-track-data-enabled={!activeCategories.has(cat.id)}
                onClick={() => toggleCategory(cat.id)}
              >
                <span
                  className={styles.categoryDot}
                  style={{ background: cat.color }}
                />
                {cat.label}
              </ChipFilter>
            ))}
          </ChipGroup>
        </div>
      )}

      <div className={styles.timelineWrapper}>
        <TimelineSlider
          value={currentTimeMs}
          max={totalTimeMs}
          onChange={handleSeek}
          pageSegments={pageSegments}
        />
        {visibleMarkers.length > 0 && (
          <div className={styles.markersOverlay}>
            {visibleMarkers.map((marker, index) => (
              <EventMarker
                key={`${marker.name}-${marker.timestamp}-${index}`}
                marker={marker}
                onClick={handleMarkerClick}
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles.controlsRow}>
        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <IconButton
            size="small"
            onClick={handlePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            data-track-id="button.playback"
            data-track-action={isPlaying ? 'pause' : 'play'}
          >
            {isPlaying ? <RiPauseLine size={18} /> : <RiPlayLine size={18} />}
          </IconButton>
        </Tooltip>
        <Typography
          variant="body2"
          color="textSecondary"
          className={styles.time}
        >
          {formatPlaybackTime(currentTimeMs)} /{' '}
          {formatPlaybackTime(totalTimeMs)}
        </Typography>
        {currentPage && (
          <>
            <div className={styles.separator} />
            <Typography
              variant="body2"
              color="textSecondary"
              className={styles.currentPageUrl}
              title={currentPage.url}
            >
              <span className={styles.currentPageLabel}>Current route:</span>
              {currentPage.url}
            </Typography>
          </>
        )}
        <FormControlLabel
          className={styles.skipInactiveLabel}
          control={
            <Switch
              size="small"
              checked={skipInactive}
              onChange={(_, checked) => handleToggleSkipInactive(checked)}
              data-track-id="switch.skip-inactive"
              data-track-action="update"
              data-track-data-enabled={!skipInactive}
            />
          }
          label="Skip inactive"
        />
        <SpeedMenu
          speed={speed}
          onSpeedChange={handleSpeedChange}
          menuContainer={menuContainer}
        />
        {fullscreenButton}
      </div>
    </div>
  );
};
